import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { AuthService } from '../services/auth.service';
import { ProfileService } from '../profile/profile.service';
import { ProfileDetails } from '../shared/interfaces/profileDetails-response.interface';

@Component({
  selector: 'app-root',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  title = '';
  loginDisplay = false;
  loginText = 'Login';
  private readonly _destroying$ = new Subject<void>();
  loginForm: FormGroup;
  returnUrl = '/dashboard';
  error = '';
  rememberMe = false;
  userDetails: ProfileDetails;
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private authService: AuthService,
    private profileService: ProfileService,
  ) {}

  ngOnInit(): void {
    // The template binds [formGroup]="loginForm" and reads loginForm.controls unconditionally,
    // so the form MUST exist before any early return below.
    this.initLoginForm();

    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';

    // "View in Intermediary App" hand-off:
    const viewToken = new URLSearchParams(window.location.search).get('viewToken');
    if (viewToken) {
      this.authService.setToken(viewToken);
      localStorage.setItem('rememberMe', 'false');
      this.getUserProfileData();
      this.router.navigateByUrl(this.returnUrl);
      return;
    }
    if (this.isLoggedIn()) {
      this.router.navigateByUrl(this.returnUrl);
    }
    this.rememberMe = this.isRememberMe();
    this.initLoginForm();
  }

  initLoginForm() {
    this.loginForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
    });
  }

  login() {
    this.loginText = 'Please Wait ...';
    this.router.navigateByUrl(this.returnUrl);
    return true;
  }

  private isLoggedIn(): boolean {
    return this.authService.isAuthenticated();
  }

  get f() {
    return this.loginForm.controls;
  }

  // Login Api Live

  onSubmit() {
    if (this.loginForm.invalid) return;
    const { username, password } = this.loginForm.value;
    this.loginText = 'Please Wait ...';
    this.authService.login({ username, password }).subscribe(
      (response) => {
        this.authService.setToken(response?.access_token);
        this.authService.rememberMe(this.rememberMe);
        this.getUserProfileData();
        this.router.navigateByUrl(this.returnUrl);
      },
      (error) => {
        console.error(error);
        this.loginText = 'Login';
        Swal.fire({
          icon: 'error',
          text: error.error.message || error.error.message,
          showConfirmButton: true,
          confirmButtonText: 'Ok',
        });
      },
    );
  }

  isRememberMe(): boolean {
    const rememberMeValue = localStorage.getItem('rememberMe');
    if (rememberMeValue !== null) {
      return JSON.parse(rememberMeValue);
    }
    return false;
  }

  onRememberMeChange(event: any) {
    this.rememberMe = event.target.checked;
  }

  getUserProfileData() {
    this.profileService.getUserProfileData().subscribe(
      (response) => {
        this.userDetails = response;
        localStorage.setItem('userData', JSON.stringify(this.userDetails));
      },
      (error) => {
        console.error(error);
        Swal.fire({
          icon: 'error',
          text: error.error.message,
          showConfirmButton: true,
          confirmButtonText: 'Ok',
        });
      },
    );
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
    // Optional-chained: the component can be destroyed before the form is built.
    this.loginForm?.reset();
  }
}